/* webpack-strip-block:removed */

import axios from 'axios';
import { orderBy as ldOrderBy } from 'lodash';

import { doBulkAjax } from 'Lib/ajax-util';
import { capitalise } from 'Lib/string-util';

/**
 * The API endpoint pertaining to tags.
 *
 * @type string
 */
export const TAGS_DATA_URI = '/api/v1/tags';

const sortTagsByLCName = tags => ldOrderBy(tags, [
  tag => tag.name.toLowerCase(),
], [
  'asc',
]);

/**
 * @typedef { Object } TagFetchParams
 * @property { ?number } per_page The number of tags to return per pagination page.
 * @property { ?boolean } full Whether or not to include related models
 * @property { ?number } limit-used-by-to The number of related models, per type (item, feed, etc.), to join via a `belongsTo` relationship, if `full` is `true`
 * @property { ?string } type If supplied, limits the query to only those tags used by this type of model
 */

/**
 * Fetches tags from the API. These are always sorted alphabetically.
 *
 * @param { ?{ url_params: ?TagFetchParams, request_data: ?Object.<string, any> } } request_config
 *
 * @returns { Promise.<Tag[]> }
 */
export const fetchTags = async (request_config) => {
  const url_params = {
    per_page: 10000,
    ...(request_config?.url_params ?? {}),
  };
  const request_data = request_config?.request_data;

  const url = new URL(`${window.location.origin}${TAGS_DATA_URI}`);
  Object.entries(url_params).forEach(([param, value]) => {
    url.searchParams.append(param, value);
  });

  try {
    const response = await axios.get(url.href, request_data);
    return sortTagsByLCName(response.data.data);
  } catch (error) {
    toastr.error("Unable to load tags.");
    console.error(error);
    console.error(error.response.data);
  }
};

/**
 * @typedef { Object } TagCreateDefinition
 * @property { string } tag The name of the new tag.
 * @property { ?boolean } admin_only Whether the tag is usable only by (super) admins.
 */

/**
 * POSTs a request to create a new `Tag` for every definition in `tag_definitions`.
 *
 * @param  { ...TagCreateDefinition } tag_definitions
 *
 */
export const createTags = (...tag_definitions) => doBulkAjax(
  tag_definitions,
  async tag_def => axios.post(TAGS_DATA_URI, tag_def),
  `Tag(s) creation successful`,
  `Unable to create new tag(s)!`,
);

/**
 * Makes parallel PATCH requests for each `Tag` in `tags_to_patch`.
 *
 * @param  { ...Tag } tags_to_patch Updated definitions for tag(s), to be updated on backend.
 */
export const patchTags = async (...tags_to_patch) => doBulkAjax(
  tags_to_patch,
  async tag => (await axios.patch(`${TAGS_DATA_URI}/${tag.uuid}`, tag)),
  'Update successful for Tag(s)',
);

/**
 * Makes parallel DELETE requests for every `Tag` in `tags_to_delete`.
 *
 * @param  { ...Tag } tags_to_delete
 */
export const destroyTags = async (...tags_to_delete) => doBulkAjax(
  tags_to_delete,
  async tag => (await axios.delete(`${TAGS_DATA_URI}/${tag.uuid}`)).data,
  `Deletion successful for Tag(s)`,
);

/**
 * @typedef { Object } TagsSyncRequestConfig
 * @property { 'item'|'feed' } target_type
 * @property { string[] } tags_to_sync_by_name Not full `Tag`s; just their names
 * @property { (Item|Feed|string)[] } targets_to_tag Full models, or uuids
 * @property { ?boolean } replace Whether or not to replace the existing tags already present on the models in `targets_to_tag`.
 */

/**
 * Syncs all given tags to all given target models (of `Item` or `Feed` type), optionally replacing existing tags on those targets.
 *
 * @param { TagsSyncRequestConfig } request_config
 */
export const syncTagsToTargets = async (request_config) => {
  const conf = {
    target_type: 'item',
    ...request_config,
  };

  const url = new URL(`${window.location.origin}${TAGS_DATA_URI}/tags-to-${conf.target_type}s`);
  const post_data = {
    items: conf.targets_to_tag,
    tags: conf.tags_to_sync_by_name,
    replace: conf.replace,
  };

  try {
    const response = await axios.post(url.href, post_data);
    toastr.success(`Successfully synced Tags to ${capitalise(conf.target_type)}(s)`);
    return response;
  } catch (err) {
    toastr.error(`Error attempting to sync Tags to ${capitalise(conf.target_type)}(s)`);
    console.error(err);
  }
};
